import React, { useState } from 'react';
import { Controller } from 'react-hook-form';
import { Accordion, Button, Form, Header, Icon, Message, Segment } from 'semantic-ui-react';
import TierLabel from '../subcomponents/TierLabel';
import { RECOMMENDATION_TIERS } from '../constants';

const InviteRow = ({ control, index, roleOptions, teamOptions, isDuplicateEmail, onRemove }) => (
    <Segment className='Onboarding__invite-row'>
        <Form.Group widths='equal'>
            <Controller
                name={`invites.${index}.first_name`}
                control={control}
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <Form.Input label='First Name' required name={name} value={value} onChange={onChange} error={error?.message} />
                )}
            />
            <Controller
                name={`invites.${index}.last_name`}
                control={control}
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <Form.Input label='Last Name' required name={name} value={value} onChange={onChange} error={error?.message} />
                )}
            />
            <Controller
                name={`invites.${index}.email`}
                control={control}
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <Form.Input
                        label='Email'
                        required
                        name={name}
                        value={value}
                        onChange={onChange}
                        error={error?.message || (isDuplicateEmail && 'This email is already on the list')}
                    />
                )}
            />
        </Form.Group>
        <Form.Group widths='equal'>
            <Controller
                name={`invites.${index}.role_id`}
                control={control}
                render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                    <Form.Select
                        label='Role'
                        required
                        name={name}
                        value={value}
                        options={roleOptions}
                        onChange={(_, { value }) => onChange({ target: { name, value } })}
                        error={error?.message}
                    />
                )}
            />
            <Controller
                name={`invites.${index}.team_ids`}
                control={control}
                render={({ field: { name, value, onChange } }) => (
                    <Form.Dropdown
                        label='Teams'
                        name={name}
                        value={value}
                        options={teamOptions}
                        onChange={(_, { value }) => onChange({ target: { name, value } })}
                        placeholder='Select teams'
                        multiple
                        selection
                    />
                )}
            />
            <Form.Field width={1} className='Onboarding__invite-remove'>
                <label>&nbsp;</label>
                <Icon name='trash alternate outline' link color='red' size='large' onClick={onRemove} aria-label='Remove invite' />
            </Form.Field>
        </Form.Group>
    </Segment>
);

const InviteTeam = ({
    control,
    invites,
    roleOptions,
    teamOptions,
    duplicateEmails,
    seatInfo,
    maxInvites,
    canContinue,
    isExtractingInvites,
    onAddInvite,
    onRemoveInvite,
    onPasteInvites,
    onBack,
    onContinue
}) => {
    const [isPasteOpen, setIsPasteOpen] = useState(false);
    const hasNoOpenSeats = !!seatInfo && maxInvites === 0;
    const [pastedText, setPastedText] = useState('');

    const addFromList = async () => {
        const wasAdded = await onPasteInvites(pastedText);
        if (wasAdded) {
            setPastedText('');
            setIsPasteOpen(false);
        }
    }

    return (
        <Segment>
            <Header as='h3'>
                Invite Your Team <TierLabel tier={RECOMMENDATION_TIERS.REQUIRES_ACTIVATION} />
                <Header.Subheader>
                    Invitations are emailed when you launch your account, not now. You can skip this and invite people later from Settings → Users.
                </Header.Subheader>
            </Header>
            {hasNoOpenSeats ? (
                <Message info size='small'>
                    <Icon name='user' />
                    {seatInfo.total_seats === 1
                        ? "Your plan has 1 seat, and it's yours."
                        : `All ${seatInfo.total_seats} seats on your plan are already in use.`}
                    {' '}To invite teammates, add seats in Settings → Billing, then invite them from Settings → Users.
                </Message>
            ) : (
                <Message info size='small' className='Onboarding__seat-usage'>
                    <Icon name='users' />
                    {seatInfo
                        ? <>Your plan has {seatInfo.total_seats} seats, {seatInfo.used_seats} already in use (including you). You can invite up to <b>{maxInvites}</b> {maxInvites === 1 ? 'person' : 'people'}: <b>{invites.length} of {maxInvites}</b> used.</>
                        : 'Invite the people who will use DYL.'}
                    {' '}New invites get a suggested role and team you can change.
                </Message>
            )}
            {invites.length > maxInvites && (
                <Message warning size='small'>
                    You have {invites.length} invites but only {maxInvites} open seat{maxInvites === 1 ? '' : 's'}. Remove {invites.length - maxInvites}, or add seats in Settings → Billing.
                </Message>
            )}

            {!hasNoOpenSeats && <Accordion>
                <Accordion.Title active={isPasteOpen} onClick={() => setIsPasteOpen(!isPasteOpen)}>
                    <Icon name='dropdown' /> Paste a list of people
                </Accordion.Title>
                <Accordion.Content active={isPasteOpen}>
                    <Form>
                        <Form.TextArea
                            value={pastedText}
                            onChange={(_, { value }) => setPastedText(value)}
                            placeholder={'One person per line, e.g.\nJane Doe <jane@acme.com>\nJohn Smith, john@acme.com\nmaria.lopez@acme.com'}
                            rows={4}
                        />
                        <Button size='small' type='button' loading={isExtractingInvites} disabled={!pastedText.trim() || isExtractingInvites} onClick={addFromList}>
                            <Icon name='magic' /> Add From List
                        </Button>
                    </Form>
                </Accordion.Content>
            </Accordion>}

            <Form noValidate className='Onboarding__invites'>
                {invites.map((invite, index) => (
                    <InviteRow
                        key={invite.id}
                        control={control}
                        index={index}
                        roleOptions={roleOptions}
                        teamOptions={teamOptions}
                        isDuplicateEmail={duplicateEmails.includes(index)}
                        onRemove={() => onRemoveInvite(index)}
                    />
                ))}
            </Form>
            {!hasNoOpenSeats && (
                <Button basic size='small' type='button' disabled={!roleOptions.length || invites.length >= maxInvites} onClick={onAddInvite}>
                    <Icon name='plus' /> Add Teammate
                </Button>
            )}
            {!hasNoOpenSeats && !roleOptions.length && (
                <Message warning size='small'>Go back and include at least one role besides Owner to invite teammates.</Message>
            )}

            <div className='Onboarding__footer'>
                <Button basic type='button' onClick={onBack}><Icon name='arrow left' /> Back</Button>
                <Button primary floated='right' type='button' disabled={!canContinue} onClick={onContinue}>
                    {invites.length ? `Review ${invites.length} Invite(s)` : 'Continue Without Inviting'} <Icon name='arrow right' />
                </Button>
                <div style={{ clear: 'both' }} />
            </div>
        </Segment>
    );
}

export default InviteTeam;
